import { getWorkspaceTheme, useWorkspaceTheme } from "./utils/workspaceTheme";
import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { API_URL } from "./config/api";

// Auth Pages
// User Pages
import Sidebar from "./pages/users/Sidebar";
import PageContainer from "./components/PageContainer";
import TopBar from "./components/TopBar";

// Admin Pages

const Login = React.lazy(() => import("./pages/auth/Login"));
const ForgotPassword = React.lazy(() => import("./pages/auth/Forgotpass"));
const SetPassword = React.lazy(() => import("./pages/auth/SetPassword"));
const LandingPage = React.lazy(() => import("./pages/LandingPage"));
const Dashboard = React.lazy(() => import("./pages/users/Dashboard"));
const InputQuestion = React.lazy(() => import("./pages/users/InputQuestion"));
const QuestionBank = React.lazy(() => import("./pages/users/QuestionBank"));
const History = React.lazy(() => import("./pages/users/History"));
const UserWorkspacePage = React.lazy(() => import("./pages/users/UserWorkspacePage"));
const UserToolsPage = React.lazy(() => import("./pages/users/UserToolsPage"));
const AdminDashboard = React.lazy(() => import("./pages/admin/admindashboard"));
const DepartmentAdminDashboard = React.lazy(() => import("./pages/admin/admindashboard").then((module) => ({ default: module.DepartmentAdminDashboard })));
export const SuperAdminDashboardFallback = React.lazy(() => import("./pages/admin/SuperAdminDashboard"));

// ---------------------------------------------------------
// 1. User Layout (Standard Sidebar)
// ---------------------------------------------------------
const MainLayout = ({ children }) => {
  const [sidebarCollapsed, setSidebarCollapsed] = React.useState(false);
  const [mobileSidebarOpen, setMobileSidebarOpen] = React.useState(false);
  const [theme, setTheme] = React.useState(getWorkspaceTheme);
  useWorkspaceTheme(theme, setTheme);
  const [connectionStatus, setConnectionStatus] = React.useState(() => {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    return {
      offline: !navigator.onLine,
      slow: ["slow-2g", "2g"].includes(connection?.effectiveType),
    };
  });

  React.useEffect(() => {
    const handleThemeUpdated = (event) => setTheme(event.detail?.theme || localStorage.getItem("bloomquest-theme") || "dark");
    const updateConnection = () => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      setConnectionStatus({
        offline: !navigator.onLine,
        slow: ["slow-2g", "2g"].includes(connection?.effectiveType),
      });
    };
    window.addEventListener("theme-updated", handleThemeUpdated);
    window.addEventListener("online", updateConnection);
    window.addEventListener("offline", updateConnection);
    navigator.connection?.addEventListener?.("change", updateConnection);
    return () => {
      window.removeEventListener("theme-updated", handleThemeUpdated);
      window.removeEventListener("online", updateConnection);
      window.removeEventListener("offline", updateConnection);
      navigator.connection?.removeEventListener?.("change", updateConnection);
    };
  }, []);

  const toggleSidebar = () => {
    setSidebarCollapsed(prev => !prev);
    setMobileSidebarOpen(prev => !prev);
  };

  return (
    <div className={`bq-shell bq-user-shell ${theme === "light" ? "bq-user-light" : "bq-user-dark"} flex h-screen w-full overflow-hidden`}>
      {mobileSidebarOpen && (
        <button
          type="button"
          aria-label="Close navigation"
          className="fixed inset-0 z-30 bg-slate-950/30 md:hidden"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}
      <Sidebar
        collapsed={sidebarCollapsed}
        mobileOpen={mobileSidebarOpen}
        onNavigate={() => setMobileSidebarOpen(false)}
        onToggleCollapsed={toggleSidebar}
      />
      <div className="bq-user-main flex h-full min-w-0 flex-1 flex-col overflow-hidden">
        <TopBar onToggleSidebar={toggleSidebar} />
        {(connectionStatus.offline || connectionStatus.slow) && (
          <div className={`bq-connection-notice ${connectionStatus.offline ? "is-offline" : ""}`} role="status" aria-live="polite">
            {connectionStatus.offline
              ? "You are offline. Reconnect to upload files or generate questions."
              : "Your connection appears slow. Uploads and question generation may take longer."}
          </div>
        )}
        <div className="bq-user-content min-h-0 flex-1 overflow-y-auto">
          <PageContainer>
            {React.isValidElement(children)
              ? React.cloneElement(children, {
                onToggleSidebar: toggleSidebar,
                theme,
                onThemeChange: (nextTheme) => {
                  localStorage.setItem("bloomquest-theme", nextTheme);
                  setTheme(nextTheme);
                  window.dispatchEvent(new CustomEvent("theme-updated", { detail: { theme: nextTheme } }));
                },
                })
              : children}
          </PageContainer>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------
// Role-Based Protection
// ---------------------------------------------------------
const getUserRole = () => {
  return localStorage.getItem("role")?.toLowerCase();
};

const ADMIN_ROLES = ["admin", "campus_admin", "department_admin"];

const AdminRoute = ({ children, academicOnly = false }) => {
  const role = getUserRole();
  const [isAuthorized, setIsAuthorized] = React.useState(ADMIN_ROLES.includes(role) ? "checking" : "denied");

  React.useEffect(() => {
    if (!role || role === "super_admin") {
      setIsAuthorized("denied");
      return;
    }

    let cancelled = false;
    fetch(`${API_URL}/admin/me`, {
      cache: "no-store",
      headers: { Authorization: `Bearer ${localStorage.getItem("token") || ""}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unauthorized");
        return response.json();
      })
      .then((data) => {
        if (!cancelled) {
          const backendRole = String(data?.role || "").toLowerCase();
          if (backendRole === "super_admin") {
            setIsAuthorized("super-admin");
            return;
          }
          if (!ADMIN_ROLES.includes(backendRole)) {
            localStorage.removeItem("token");
            localStorage.removeItem("role");
            setIsAuthorized("denied");
            return;
          }
          localStorage.setItem("name", data?.name || "");
          localStorage.setItem("email", data?.email || "");
          localStorage.setItem("campus_name", data?.campus_name || "");
          localStorage.setItem("role", backendRole);
          setIsAuthorized("allowed");
        }
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem("token");
          localStorage.removeItem("role");
          setIsAuthorized("denied");
        }
      });

    return () => { cancelled = true; };
  }, [role]);

  if (!role) return <Navigate to="/" replace />;
  if (role === "super_admin" || isAuthorized === "super-admin") return <Navigate to="/super-admin/dashboard" replace />;
  if (isAuthorized === "checking") return <div className="flex h-screen items-center justify-center text-sm text-slate-500">Checking admin access…</div>;
  if (isAuthorized === "allowed" && role === "department_admin" && !academicOnly) {
    return <Navigate to="/admin/academic" replace />;
  }
  return isAuthorized === "allowed" ? children : <Navigate to="/dashboard" replace />;
};

const SuperAdminRoute = ({ children }) => {
  const role = getUserRole();
  const [authorization, setAuthorization] = React.useState(() => (
    role
      ? { status: "checking", role: null }
      : { status: "denied", role: null }
  ));

  React.useEffect(() => {
    if (!role) {
      setAuthorization({ status: "denied", role: null });
      return undefined;
    }

    let cancelled = false;
    fetch(`${API_URL}/admin/me`, {
      cache: "no-store",
      headers: { Authorization: `******"token") || ""}` },
    })
      .then((response) => {
        if (!response.ok) throw new Error("Unauthorized");
        return response.json();
      })
      .then((data) => {
        if (cancelled) return;

        const backendRole = String(data?.role || "").toLowerCase();
        if (!["super_admin", ...ADMIN_ROLES].includes(backendRole)) {
          localStorage.removeItem("token");
          localStorage.removeItem("role");
          setAuthorization({ status: "denied", role: null });
          return;
        }

        localStorage.setItem("role", backendRole);
        setAuthorization({ status: "allowed", role: backendRole });
      })
      .catch(() => {
        if (!cancelled) {
          localStorage.removeItem("token");
          localStorage.removeItem("role");
          setAuthorization({ status: "denied", role: null });
        }
      });

    return () => { cancelled = true; };
  }, [role]);

  if (!role) return <Navigate to="/" replace />;
  if (authorization.status === "checking") {
    return <div className="flex h-screen items-center justify-center text-sm text-slate-500">Checking admin access…</div>;
  }
  if (authorization.status !== "allowed") return <Navigate to="/" replace />;
  if (authorization.role === "super_admin") return children;
  return ["campus_admin", "admin"].includes(authorization.role)
    ? <Navigate to="/admin/dashboard" replace />
    : authorization.role === "department_admin"
      ? <Navigate to="/admin/academic" replace />
    : <Navigate to="/dashboard" replace />;
};

const UserRoute = ({ children }) => {
  const role = getUserRole();
  if (!role) return <Navigate to="/" replace />;
  if (role === "faculty" && localStorage.getItem("must_change_password") === "true") {
    return <Navigate to="/set-password?force=1" replace />;
  }
  if (role === "super_admin") return <Navigate to="/super-admin/dashboard" replace />;
  return ["campus_admin", "admin"].includes(role)
    ? <Navigate to="/admin/dashboard" replace />
    : role === "department_admin"
      ? <Navigate to="/admin/academic" replace />
      : children;
};

const QuestionBankRoute = () => {
  if (["admin", "campus_admin", "department_admin"].includes(getUserRole())) {
    return (
      <AdminRoute>
        <PageContainer>
          <AdminDashboard />
        </PageContainer>
      </AdminRoute>
    );
  }

  return (
    <UserRoute>
      <MainLayout>
        <QuestionBank />
      </MainLayout>
    </UserRoute>
  );
};

const AdminAcademicRoute = () => (
  <AdminRoute academicOnly>
    <PageContainer>
      {getUserRole() === "department_admin"
        ? <DepartmentAdminDashboard />
        : <AdminDashboard />}
    </PageContainer>
  </AdminRoute>
);

// ---------------------------------------------------------
// Main App Router
// ---------------------------------------------------------
function App() {
  React.useEffect(() => {
    const confirmPageRefresh = (event) => {
      if (window.__bqSkipRefreshPrompt) {
        window.__bqSkipRefreshPrompt = false;
        return;
      }
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", confirmPageRefresh);
    return () => window.removeEventListener("beforeunload", confirmPageRefresh);
  }, []);

  return (
    <BrowserRouter>
      <React.Suspense fallback={<div className="flex min-h-screen items-center justify-center text-sm text-slate-500">Loading…</div>}>
        <Routes>
        {/* Auth Routes */}
        <Route path="/" element={<LandingPage />} />
        <Route path="/home" element={<LandingPage />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/set-password" element={<SetPassword />} />
        
        {/* ========================================= */}
        {/* USER ROUTES                               */}
        {/* ========================================= */}
        <Route 
          path="/dashboard" 
          element={
            <UserRoute>
              <MainLayout>
                <Dashboard />
              </MainLayout>
            </UserRoute>
          } 
        />
        <Route 
          path="/input" 
          element={
            <UserRoute>
              <MainLayout>
                <InputQuestion />
              </MainLayout>
            </UserRoute>
          } 
        />
        <Route 
          path="/question-bank" 
          element={<QuestionBankRoute />}
        />
        <Route
          path="/question-bank/:subjectId"
          element={<QuestionBankRoute />}
        />
        <Route
          path="/history"
          element={
            <UserRoute>
              <MainLayout>
                <History />
              </MainLayout>
            </UserRoute>
          } 
        />
        <Route path="/analytics" element={<Navigate to="/dashboard" replace />} />
        <Route
          path="/settings"
          element={
            <UserRoute>
              <MainLayout>
                <UserWorkspacePage section="settings" />
              </MainLayout>
            </UserRoute>
          }
        />
        {[
          ["/assessments", "assessments"],
          ["/favorites", "favorites"],
          ["/recycle-bin", "recycle"],
          ["/subjects", "subjects"],
          ["/notifications", "notifications"],
          ["/imports", "imports"],
          ["/system-status", "status"],
          ["/help", "help"],
        ].map(([path, section]) => (
          <Route
            key={path}
            path={path}
            element={
              <UserRoute>
                <MainLayout>
                  <UserToolsPage section={section} />
                </MainLayout>
              </UserRoute>
            }
          />
        ))}

        {/* ========================================= */}
        {/* ADMIN ROUTES                              */}
        {/* ========================================= */}

        <Route path="/super-admin" element={<Navigate to="/super-admin/dashboard" replace />} />
        <Route path="/super-admin/academic" element={<SuperAdminRoute><PageContainer><AdminDashboard /></PageContainer></SuperAdminRoute>} />
        <Route path="/super-admin/academic/campus/:campusId" element={<SuperAdminRoute><PageContainer><AdminDashboard /></PageContainer></SuperAdminRoute>} />
        <Route path="/super-admin/academic/campus/:campusId/department/:departmentId" element={<SuperAdminRoute><PageContainer><AdminDashboard /></PageContainer></SuperAdminRoute>} />
        <Route path="/super-admin/academic/campus/:campusId/department/:departmentId/program/:programId" element={<SuperAdminRoute><PageContainer><AdminDashboard /></PageContainer></SuperAdminRoute>} />
        <Route path="/super-admin/questions" element={<SuperAdminRoute><PageContainer><AdminDashboard /></PageContainer></SuperAdminRoute>} />
        <Route path="/super-admin/*" element={<SuperAdminRoute><PageContainer><AdminDashboard /></PageContainer></SuperAdminRoute>} />
        
        {/* Redirect base /admin to the admin dashboard */}
        <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

        <Route 
          path="/admin/dashboard" 
          element={
            <AdminRoute>
              <PageContainer>
                <AdminDashboard />
              </PageContainer>
            </AdminRoute>
          } 
        />
        <Route path="/admin/academic" element={<AdminAcademicRoute />} />
        <Route path="/admin/academic/campus/:campusId" element={<AdminAcademicRoute />} />
        <Route path="/admin/academic/campus/:campusId/department/:departmentId" element={<AdminAcademicRoute />} />
        <Route path="/admin/academic/campus/:campusId/department/:departmentId/program/:programId" element={<AdminAcademicRoute />} />
        <Route path="/admin/questions" element={<AdminAcademicRoute />} />
        <Route path="/admin/questions/:subjectId" element={<AdminAcademicRoute />} />
        <Route path="/admin/users" element={<AdminAcademicRoute />} />

        <Route
          path="/admin/users/:userId"
          element={
            <AdminRoute>
              <PageContainer>
                <AdminDashboard />
              </PageContainer>
            </AdminRoute>
          }
        />
        <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </React.Suspense>
    </BrowserRouter>
  );
}

export default App;